import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  FileText,
  Loader2,
  Sparkles,
  Upload,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { usePortfolio } from "@/hooks/usePortfolio";

const MAX_PHOTO_MB = 5;
const MAX_CV_MB = 10;

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export function PortfolioSection({ programmeId }: { programmeId: string | null }) {
  const {
    portfolio,
    skillOptions,
    isLoading,
    error,
    save,
    uploadPhoto,
    uploadCv,
    publish,
    unpublish,
  } = usePortfolio(programmeId);
  const [isOpen, setIsOpen] = useState(true);
  const [actionError, setActionError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [isUploadingCv, setIsUploadingCv] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);

  const [headline, setHeadline] = useState("");
  const [firm, setFirm] = useState("");
  const [bio, setBio] = useState("");
  const [linkedinUrl, setLinkedinUrl] = useState("");
  const [skills, setSkills] = useState<string[]>([]);

  const photoInputRef = useRef<HTMLInputElement>(null);
  const cvInputRef = useRef<HTMLInputElement>(null);

  // Only seed the text fields from the fetched portfolio once per programme —
  // not on every `portfolio` update, since photo/CV uploads and publish/unpublish
  // also replace `portfolio` and would otherwise clobber unsaved edits below.
  const syncedProgrammeRef = useRef<string | null>(null);
  useEffect(() => {
    if (isLoading || syncedProgrammeRef.current === programmeId) return;
    setHeadline(portfolio?.headline ?? "");
    setFirm(portfolio?.firm ?? "");
    setBio(portfolio?.bio ?? "");
    setLinkedinUrl(portfolio?.linkedin_url ?? "");
    setSkills(portfolio?.skills ?? []);
    syncedProgrammeRef.current = programmeId;
  }, [isLoading, portfolio, programmeId]);

  if (!programmeId) return null;

  const displayError = error || actionError;

  const runAction = async (fn: () => Promise<unknown>, setBusy: (b: boolean) => void) => {
    try {
      setActionError(null);
      setBusy(true);
      await fn();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const minSkills = skillOptions?.min_selected ?? 3;
  const maxSkills = skillOptions?.max_selected ?? 6;
  const skillCountIsInvalid = skills.length > 0 && skills.length < minSkills;

  const toggleSkill = (skill: string) => {
    setSkills((current) => {
      if (current.includes(skill)) return current.filter((s) => s !== skill);
      if (current.length >= maxSkills) return current;
      return [...current, skill];
    });
  };

  const handleSave = () => {
    // The API rejects a part-filled selection too; catching it here keeps the
    // rest of the form from bouncing off a 400 the participant can't see.
    if (skillCountIsInvalid) {
      setActionError(`Choose at least ${minSkills} skills, or none at all`);
      return;
    }
    return runAction(
      () =>
        save({
          headline: headline || null,
          firm: firm || null,
          bio: bio || null,
          linkedin_url: linkedinUrl || null,
          skills,
        }),
      setIsSaving
    );
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > MAX_PHOTO_MB * 1024 * 1024) {
      setActionError(`Photo exceeds ${MAX_PHOTO_MB} MB limit`);
      return;
    }
    runAction(() => uploadPhoto(file), setIsUploadingPhoto);
  };

  const handleCvChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > MAX_CV_MB * 1024 * 1024) {
      setActionError(`CV exceeds ${MAX_CV_MB} MB limit`);
      return;
    }
    runAction(() => uploadCv(file), setIsUploadingCv);
  };

  const handlePublish = () => runAction(() => publish(true), setIsPublishing);
  const handleUnpublish = () => runAction(() => unpublish(), setIsPublishing);

  const subtitle = portfolio?.is_published
    ? "Published — visible on Live Portfolios"
    : portfolio
      ? "Draft — not visible publicly"
      : "Not started";

  return (
    <div className="overflow-hidden rounded-[14px] border border-content-border bg-card">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center gap-4 px-7 py-5 text-left transition-colors hover:bg-content-subtle"
      >
        <div className="flex h-11 w-11 flex-none items-center justify-center rounded-[11px] bg-[#E9F1E6]">
          <Sparkles className="h-[19px] w-[19px] text-[#1E6F4F]" strokeWidth={1.7} />
        </div>
        <div className="min-w-0">
          <div className="text-base font-bold text-card-foreground">Portfolio</div>
          <div className="mt-0.5 truncate text-[13px] text-content-muted">{subtitle}</div>
        </div>
        <ChevronDown
          className={`ml-auto h-[15px] w-[15px] flex-none text-content-muted transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
          strokeWidth={2}
        />
      </button>

      {isOpen && (
        <>
          {displayError && (
            <div className="flex items-center gap-3 border-t border-content-border/60 bg-red-50 px-7 py-4 text-sm text-red-700">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {displayError}
            </div>
          )}

          {isLoading ? (
            <div className="flex justify-center border-t border-content-border/60 py-8">
              <Loader2 className="h-5 w-5 animate-spin text-content-muted" />
            </div>
          ) : (
            <>
              <div className="border-t border-content-border/60 px-7 py-[22px]">
                <p className="mb-5 font-serif text-[13px] italic text-content-muted">
                  Tailor how you appear on the public Live Portfolios page. Nothing here is visible
                  to anyone until you publish it below.
                </p>

                <div className="flex flex-wrap items-center gap-4">
                  {portfolio?.photo_url ? (
                    <img
                      src={portfolio.photo_url}
                      alt="Portfolio photo"
                      className="h-16 w-16 flex-none rounded-full object-cover"
                    />
                  ) : (
                    <div className="flex h-16 w-16 flex-none items-center justify-center rounded-full bg-content-subtle text-[11px] font-bold text-content-muted">
                      No photo
                    </div>
                  )}
                  <div>
                    <button
                      onClick={() => photoInputRef.current?.click()}
                      disabled={isUploadingPhoto}
                      className="flex items-center gap-2 rounded-[9px] border border-content-border bg-card px-4 py-2 text-[12.5px] font-bold text-content-foreground transition-colors hover:border-brand/50 disabled:opacity-60"
                    >
                      {isUploadingPhoto ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Upload className="h-3.5 w-3.5" />
                      )}
                      {portfolio?.photo_url ? "Replace photo" : "Upload photo"}
                    </button>
                    <p className="mt-1.5 text-[11.5px] text-content-muted">
                      JPEG, PNG or WebP, up to {MAX_PHOTO_MB} MB
                    </p>
                  </div>
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={handlePhotoChange}
                  />
                </div>

                <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-[.1em] text-content-muted">
                      Headline
                    </label>
                    <input
                      value={headline}
                      onChange={(e) => setHeadline(e.target.value)}
                      placeholder="e.g. Equity Research Analyst"
                      className="w-full rounded-[9px] border border-content-border bg-card px-3.5 py-2.5 text-[14px] text-card-foreground outline-none focus:border-brand/60"
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-[.1em] text-content-muted">
                      Firm
                    </label>
                    <input
                      value={firm}
                      onChange={(e) => setFirm(e.target.value)}
                      placeholder="e.g. Acme Capital"
                      className="w-full rounded-[9px] border border-content-border bg-card px-3.5 py-2.5 text-[14px] text-card-foreground outline-none focus:border-brand/60"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-[.1em] text-content-muted">
                    Bio
                  </label>
                  <textarea
                    value={bio}
                    onChange={(e) => setBio(e.target.value)}
                    rows={4}
                    placeholder="A short bio for your public profile"
                    className="w-full resize-y rounded-[9px] border border-content-border bg-card px-3.5 py-2.5 text-[14px] text-card-foreground outline-none focus:border-brand/60"
                  />
                </div>

                {skillOptions && (
                  <div className="mt-4">
                    <div className="mb-1.5 flex flex-wrap items-baseline gap-x-3">
                      <span className="text-[11px] font-bold uppercase tracking-[.1em] text-content-muted">
                        Skills
                      </span>
                      <span className="text-[11.5px] text-content-muted">
                        Pick {minSkills}–{maxSkills} · {skills.length} selected
                      </span>
                    </div>
                    <p className="mb-3 text-[11.5px] text-content-muted">
                      They appear on your public profile in a fixed order, not the order you pick
                      them.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {skillOptions.skills.map((skill) => {
                        const isSelected = skills.includes(skill);
                        const isFull = !isSelected && skills.length >= maxSkills;
                        return (
                          <button
                            key={skill}
                            type="button"
                            aria-pressed={isSelected}
                            onClick={() => toggleSkill(skill)}
                            disabled={isFull}
                            className={`rounded-full border px-3.5 py-1.5 text-[12.5px] transition-colors ${
                              isSelected
                                ? "border-[#1E6F4F] bg-[#E9F1E6] font-bold text-[#1E6F4F]"
                                : "border-content-border bg-card text-content-foreground hover:border-brand/50"
                            } ${isFull ? "cursor-not-allowed opacity-40" : ""}`}
                          >
                            {skill}
                          </button>
                        );
                      })}
                    </div>
                    {skillCountIsInvalid && (
                      <p className="mt-2 text-[11.5px] text-red-700">
                        Choose at least {minSkills} skills, or none at all.
                      </p>
                    )}
                    {portfolio?.is_published && portfolio.skills.length === 0 && (
                      <p className="mt-2 text-[11.5px] text-content-muted">
                        Your published profile has no skills yet — pick some and save to add the
                        section.
                      </p>
                    )}
                  </div>
                )}

                <div className="mt-4">
                  <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-[.1em] text-content-muted">
                    LinkedIn
                  </label>
                  <input
                    value={linkedinUrl}
                    onChange={(e) => setLinkedinUrl(e.target.value)}
                    placeholder="https://www.linkedin.com/in/you"
                    className="w-full rounded-[9px] border border-content-border bg-card px-3.5 py-2.5 text-[14px] text-card-foreground outline-none focus:border-brand/60"
                  />
                </div>

                <div className="mt-5 flex flex-wrap items-center gap-4">
                  <button
                    onClick={() => cvInputRef.current?.click()}
                    disabled={isUploadingCv}
                    className="flex items-center gap-2 rounded-[9px] border border-content-border bg-card px-4 py-2 text-[12.5px] font-bold text-content-foreground transition-colors hover:border-brand/50 disabled:opacity-60"
                  >
                    {isUploadingCv ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <FileText className="h-3.5 w-3.5" />
                    )}
                    {portfolio?.cv_url ? "Replace CV" : "Upload CV"}
                  </button>
                  {portfolio?.cv_url && (
                    <a
                      href={portfolio.cv_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[12.5px] font-bold text-[#1E6F4F] hover:text-brand"
                    >
                      View current CV ↓
                    </a>
                  )}
                  <span className="text-[11.5px] text-content-muted">
                    PDF, up to {MAX_CV_MB} MB
                  </span>
                  <input
                    ref={cvInputRef}
                    type="file"
                    accept="application/pdf"
                    className="hidden"
                    onChange={handleCvChange}
                  />
                </div>

                <div className="mt-6">
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="flex items-center gap-2 rounded-[10px] bg-[#013220] px-5 py-3 text-[13px] font-bold text-[#F2EFE3] transition-colors hover:bg-[#0A4530] disabled:opacity-60"
                  >
                    {isSaving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                    Save details
                  </button>
                </div>
              </div>

              <div className="border-t border-content-border/60 bg-content-subtle px-7 py-[22px]">
                {portfolio?.is_published ? (
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-2.5 text-[13.5px] font-bold text-[#1E6F4F]">
                      <CheckCircle2 className="h-4 w-4" />
                      Published
                      {portfolio.published_at && ` on ${formatDate(portfolio.published_at)}`}
                    </div>
                    <button
                      onClick={handleUnpublish}
                      disabled={isPublishing}
                      className="rounded-[9px] border border-content-border bg-card px-4 py-2 text-[12.5px] font-bold text-content-foreground transition-colors hover:border-brand/50 disabled:opacity-60"
                    >
                      {isPublishing ? "Unpublishing…" : "Unpublish"}
                    </button>
                  </div>
                ) : (
                  <>
                    <label className="flex items-start gap-3 text-[13px] text-content-foreground">
                      <input
                        type="checkbox"
                        checked={consentChecked}
                        onChange={(e) => setConsentChecked(e.target.checked)}
                        className="mt-0.5 h-4 w-4 flex-none accent-[#1E6F4F]"
                      />
                      I consent to my name, photo, bio, skills, CV and LinkedIn link being shown
                      publicly on the Live Portfolios page.
                    </label>
                    <button
                      onClick={handlePublish}
                      disabled={!consentChecked || isPublishing || !portfolio?.bio?.trim()}
                      className="mt-4 flex items-center gap-2 rounded-[10px] bg-[#013220] px-5 py-3 text-[13px] font-bold text-[#F2EFE3] transition-colors hover:bg-[#0A4530] disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      {isPublishing && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                      Publish portfolio
                    </button>
                    {!portfolio?.bio?.trim() && (
                      <p className="mt-2 text-[11.5px] text-content-muted">
                        Add and save a bio before you can publish.
                      </p>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
